import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import BackupIcon from "@mui/icons-material/Backup";
import RefreshIcon from "@mui/icons-material/Refresh";
import httpRequest from "../utils/httpRequest";

const DatabaseBackup = () => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [lastResult, setLastResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await httpRequest("maintenance/backup/logs", "GET");
      setLogs(response.logs || []);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch backup logs.";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setLogsLoading(false);
    }
  };

  const handleTakeBackup = async () => {
    setLoading(true);

    try {
      const response = await httpRequest("maintenance/backup/run", "POST");
      setLastResult(response);
      await fetchLogs();
      setSnackbar({
        open: true,
        message: response.message || "Backup completed successfully.",
        severity: "success",
      });
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to run backup.";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(15,23,42,0.02) 100%)",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                backgroundColor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <BackupIcon />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Database Backup
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a Git-backed dump of the database configured in `DB_NAME`.
              </Typography>
            </Box>
          </Stack>

          <Typography variant="body1" color="text.secondary">
            The backup job creates a compressed dump, keeps only the latest 3
            backup files in the private GitLab repo, and force-pushes the
            rewritten branch so the repository stays small.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              size="large"
              onClick={handleTakeBackup}
              disabled={loading}
              startIcon={<BackupIcon />}
              sx={{ textTransform: "none", px: 3 }}
            >
              {loading ? "Taking Backup..." : "Take Backup Now"}
            </Button>
            <Chip
              label="Admin only"
              color="primary"
              variant="outlined"
              sx={{ alignSelf: "center" }}
            />
            <Button
              variant="outlined"
              size="large"
              onClick={fetchLogs}
              disabled={logsLoading}
              startIcon={<RefreshIcon />}
              sx={{ textTransform: "none" }}
            >
              {logsLoading ? "Refreshing..." : "Refresh Logs"}
            </Button>
          </Stack>

          {lastResult && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Last run details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Backup file: {lastResult.backupFile}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pushed ref: {lastResult.pushedRef}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Kept files: {lastResult.keptFiles?.join(", ")}
              </Typography>
            </Paper>
          )}

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Recent Backup Logs
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No backup logs yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                        <TableCell>{log.status}</TableCell>
                        <TableCell>{log.requestedBy || "N/A"}</TableCell>
                        <TableCell>
                          {typeof log.durationMs === "number"
                            ? `${(log.durationMs / 1000).toFixed(1)}s`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {log.status === "FAILED"
                            ? log.error || "Failed"
                            : log.backupFile || "Started"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DatabaseBackup;
